import { useCallback, useEffect, useRef, useState } from 'react';
import { useCancelledOnUnmount } from './use-cancelled.ts';
import { api, ApiError } from '../api/client.ts';
import { t } from '../i18n/index.ts';
import { startDownload, waitForExport } from './download.ts';

export type ExportPhase = 'idle' | 'sending' | 'preparing' | 'done' | 'failed';

export interface ExportRequest {
  streamId: number;
  fromMs: number;
  toMs: number;
  format: string;
  label: string | null;
}

/**
 * Asking for a cut and handing over the file.
 *
 * Shared by the editor and the block row, so "cut and download" and "download
 * the whole half hour" behave the same: one press, then the file, without a
 * detour through another screen.
 */
export function useExportDownload(): {
  phase: ExportPhase;
  error: string | null;
  run: (request: ExportRequest) => Promise<void>;
  reset: () => void;
} {
  const [phase, setPhase] = useState<ExportPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  // Stops the wait if the component goes away mid-request.
  const cancelled = useCancelledOnUnmount();

  const run = useCallback(async (request: ExportRequest): Promise<void> => {
    setPhase('sending');
    setError(null);
    try {
      const created = await api.createExport({
        stream_id: request.streamId,
        range_start_at: new Date(request.fromMs).toISOString(),
        range_end_at: new Date(request.toMs).toISOString(),
        output_format: request.format,
        label: request.label,
      });

      // The same cut asked for twice is already made (see D20); no wait needed.
      if (created.status === 'ready' && created.download_url) {
        startDownload(created.download_url);
        setPhase('done');
        return;
      }

      setPhase('preparing');
      const outcome = await waitForExport(created.id, () => cancelled.current);

      if (outcome.kind === 'ready' && outcome.record.download_url) {
        startDownload(outcome.record.download_url);
        setPhase('done');
      } else if (outcome.kind === 'failed') {
        setPhase('failed');
        setError(outcome.message ?? t('editor.export_failed'));
      } else {
        // Still working, or abandoned. It will be waiting on the exports screen.
        setPhase('done');
      }
    } catch (err) {
      setPhase('failed');
      setError(
        err instanceof ApiError && err.code === 'range_unavailable'
          ? t('error.range_unavailable')
          : t('editor.export_failed'),
      );
    }
  }, []);

  const reset = useCallback(() => {
    setPhase('idle');
    setError(null);
  }, []);

  return { phase, error, run, reset };
}
